import { useCallback, useState } from 'react';
import { Popover } from 'radix-ui';
import { formatCount } from '@/i18n/format';
import {
  Menu,
  MenuContent,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/menu';
import { WorkspaceIcon } from '@/workspace/icons';
import type { PersonUsage } from '@/billing/api';
import { addDays, credits, daysBetween, dayTime, isoDay, type Formatters } from './format';
import type { UsageCopy } from './copy';
import { Avatar } from './people-list';

export interface Range {
  from: string;
  to: string;
}

type Open = '' | 'from' | 'to' | 'person';

/** Popovers portal into the trigger's workspace shell so they keep its palette. */
function useShellHost() {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const anchor = useCallback((node: HTMLElement | null) => {
    if (node) setHost(node.closest<HTMLElement>('.ws-shell'));
  }, []);
  return [host, anchor] as const;
}

const MAX_DAYS = 366;

function monthCells(year: number, month: number) {
  const first = new Date(Date.UTC(year, month, 1));
  const lead = (first.getUTCDay() + 6) % 7;
  const length = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return {
    lead,
    days: Array.from({ length }, (_, i) => isoDay(new Date(Date.UTC(year, month, i + 1)))),
  };
}

function Calendar({
  which,
  range,
  today,
  presets,
  f,
  copy,
  onPick,
}: {
  which: 'from' | 'to';
  range: Range;
  today: string;
  presets: Array<{ label: string; range: Range }>;
  f: Formatters;
  copy: UsageCopy;
  onPick: (range: Range) => void;
}) {
  const anchor = new Date(dayTime(which === 'to' ? range.to : range.from));
  const [cursor, setCursor] = useState({
    year: anchor.getUTCFullYear(),
    month: anchor.getUTCMonth(),
  });
  const { lead, days } = monthCells(cursor.year, cursor.month);
  const earliest = addDays(today, -(MAX_DAYS * 2));
  const step = (direction: number) =>
    setCursor(({ year, month }) => {
      const next = new Date(Date.UTC(year, month + direction, 1));
      return { year: next.getUTCFullYear(), month: next.getUTCMonth() };
    });
  const nextOff = isoDay(new Date(Date.UTC(cursor.year, cursor.month + 1, 1))) > today;
  const prevOff = isoDay(new Date(Date.UTC(cursor.year, cursor.month, 0))) < earliest;
  const pick = (day: string) => {
    if (which === 'from')
      onPick({
        from: day,
        to: daysBetween(day, range.to) >= MAX_DAYS ? addDays(day, MAX_DAYS - 1) : range.to,
      });
    else
      onPick({
        from: daysBetween(range.from, day) >= MAX_DAYS ? addDays(day, -(MAX_DAYS - 1)) : range.from,
        to: day,
      });
  };
  return (
    <>
      <div className="cu-cal-head">
        <span className="cu-cal-title">{f.monthTitle(cursor.year, cursor.month)}</span>
        <span className="cu-cal-nav">
          <button
            type="button"
            className="cu-icon-btn is-prev"
            aria-label={copy.previousMonth}
            disabled={prevOff}
            onClick={() => step(-1)}
          >
            <WorkspaceIcon name="chevron" size={16} />
          </button>
          <button
            type="button"
            className="cu-icon-btn"
            aria-label={copy.nextMonth}
            disabled={nextOff}
            onClick={() => step(1)}
          >
            <WorkspaceIcon name="chevron" size={16} />
          </button>
        </span>
      </div>
      <div className="cu-cal-grid">
        {f.weekdays.map((day, i) => (
          <span className="cu-cal-dow" key={i} aria-hidden="true">
            {day}
          </span>
        ))}
        {Array.from({ length: lead }, (_, i) => (
          <span className="cu-cal-day cu-cal-blank" key={`blank-${i}`} />
        ))}
        {days.map((day) => {
          const available =
            day <= today &&
            day >= earliest &&
            (which === 'to' ? day >= range.from : day <= range.to);
          const end = day === range.from || day === range.to;
          const inside = day > range.from && day < range.to;
          return (
            <button
              type="button"
              key={day}
              className={`cu-cal-day${end ? ' is-end' : inside ? ' is-in' : ''}${day === today ? ' is-today' : ''}`}
              disabled={!available}
              aria-label={f.longDay(day)}
              aria-pressed={end}
              onClick={() => pick(day)}
            >
              {new Date(dayTime(day)).getUTCDate()}
            </button>
          );
        })}
      </div>
      <div className="cu-cal-presets">
        {presets.map((preset) => (
          <button
            type="button"
            key={preset.label}
            className={`cu-chip-btn${preset.range.from === range.from && preset.range.to === range.to ? ' is-on' : ''}`}
            onClick={() => onPick(preset.range)}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </>
  );
}

function PersonMenu({
  people,
  colorOf,
  selectedId,
  f,
  copy,
  onPick,
}: {
  people: PersonUsage[];
  colorOf: (userId: string) => string;
  selectedId: string | null;
  f: Formatters;
  copy: UsageCopy;
  onPick: (userId: string | null) => void;
}) {
  return (
    <MenuContent label={copy.personMenu} align="start" className="cu-person-menu cu-portal">
      <MenuRadioGroup
        value={selectedId ?? ''}
        onValueChange={(value) => onPick(value === '' ? null : value)}
      >
        <MenuRadioItem
          value=""
          icon={
            <span className="cu-menu-icon">
              <WorkspaceIcon name="users" size={15} />
            </span>
          }
          description={formatCount(copy.peopleNoun, people.length, f.locale)}
        >
          {copy.allUsers}
        </MenuRadioItem>
        <MenuSeparator />
        {people.map((person) => (
          <MenuRadioItem
            key={person.userId}
            value={person.userId}
            icon={<Avatar name={person.name} color={colorOf(person.userId)} size="sm" />}
            description={`${f.int(credits(person.creditMicros))} ${copy.creditsUnit}`}
          >
            {person.name}
          </MenuRadioItem>
        ))}
      </MenuRadioGroup>
    </MenuContent>
  );
}

export function Toolbar({
  range,
  today,
  presets,
  people,
  colorOf,
  selectedId,
  showPeople,
  updated,
  f,
  copy,
  onRange,
  onPerson,
}: {
  range: Range;
  today: string;
  presets: Array<{ label: string; range: Range }>;
  people: PersonUsage[];
  colorOf: (userId: string) => string;
  selectedId: string | null;
  showPeople: boolean;
  updated: string;
  f: Formatters;
  copy: UsageCopy;
  onRange: (range: Range) => void;
  onPerson: (userId: string | null) => void;
}) {
  const [open, setOpen] = useState<Open>('');
  const [host, anchor] = useShellHost();
  const selected = people.find((person) => person.userId === selectedId) ?? null;
  const field = (name: 'from' | 'to', label: string, value: string) => (
    <div className="cu-anchor">
      <Popover.Root
        open={open === name}
        onOpenChange={(next) => setOpen(next ? name : '')}
      >
        <Popover.Trigger asChild>
          <button
            type="button"
            ref={anchor}
            className={`cu-field-btn${open === name ? ' is-open' : ''}`}
          >
            <span className="cu-field-label">{label}</span>
            <span className="cu-field-value cu-num">{f.day(value)}</span>
            <WorkspaceIcon name="down" size={14} className="cu-chev" />
          </button>
        </Popover.Trigger>
        <Popover.Portal container={host ?? undefined}>
          <Popover.Content
            className="cu-pop cu-cal cu-portal pc-portal"
            align={name === 'to' ? 'end' : 'start'}
            sideOffset={8}
            collisionPadding={16}
            aria-label={name === 'from' ? copy.chooseFirstDay : copy.chooseLastDay}
          >
            <Calendar
              which={name}
              range={range}
              today={today}
              presets={presets}
              f={f}
              copy={copy}
              onPick={(next) => {
                setOpen('');
                onRange(next);
              }}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
  return (
    <div className="cu-toolbar">
      {field('from', copy.from, range.from)}
      {field('to', copy.to, range.to)}
      {showPeople && (
        <>
          <span className="cu-toolbar-sep" />
          <div className="cu-anchor is-wide">
            <Menu open={open === 'person'} onOpenChange={(next) => setOpen(next ? 'person' : '')}>
              <MenuTrigger asChild>
                <button
                  type="button"
                  className={`cu-field-btn${open === 'person' ? ' is-open' : ''}`}
                >
                  <span className="cu-field-label">{copy.person}</span>
                  {selected && (
                    <Avatar name={selected.name} color={colorOf(selected.userId)} size="sm" />
                  )}
                  <span className="cu-field-value">
                    {selected ? selected.name : copy.allUsers}
                  </span>
                  <WorkspaceIcon name="down" size={14} className="cu-chev" />
                </button>
              </MenuTrigger>
              <PersonMenu
                people={people}
                colorOf={colorOf}
                selectedId={selectedId}
                f={f}
                copy={copy}
                onPick={(userId) => {
                  setOpen('');
                  onPerson(userId);
                }}
              />
            </Menu>
          </div>
        </>
      )}
      <span className="cu-updated">
        <span className="cu-dot" />
        {updated}
      </span>
    </div>
  );
}
